import { useCallback, useContext, useEffect, useMemo } from 'react';
import playerService from '../../../helpers/bitmovinPlayer';
import { UIManager } from 'bitmovin-player-ui';
import { useLoadTooltips } from '../hooks/useLoadTooltips';
import useLanguage from '../../../hooks/useLanguage';
import {
    checkDRMSupported,
    handleError,
    onSuccess,
    setAudioLanguage,
    setSubtitleLanguage,
} from '../utils/PlayerUtills';
import attachEvents from '../utils/attachEvents';
import { __ } from '../../../translations';
import remoteLogger from '../../../helpers/RemoteLogger';
import { log } from '@intigral/utils';
import { PlayerAPI, LogLevel } from 'bitmovin-player';
import { playerTypes } from '../constants';
import { setYBPluginOptions, updateYouboraAnalytics, setYBAdPluginOptions } from '../../../helpers/youbora';
import { useSelector } from 'react-redux';
import { IStore } from '../../../interfaces/StoreTypes';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { KochavaContext } from '../../../contexts/KochavaContext';
import { getItemProperties } from '../../../contexts/MixpanelHelper';
import { EVENT } from '../../../contexts/MixpanelConstants';
import cdnHandler from '../../../helpers/CDNHandler';
import f from '../../../helpers/formatter2';
import { UsePlayerHandlerResults, UsePlayerHandlersParams } from '../types/IPlayer';
import { Genesys } from '../../../components/ui/Genesys';
import { sendKochava, sendLiveTvKochava } from '../../../helpers/analytics';
import { playerSSAIManager } from '../../../utils/SSAI';
import { isPlayerAlive, safePlayerPlay } from '../../../helpers/playerGuards';

export const usePlayerHandler = ({
    playerInitializedRef,
    playerInstanceRef,
    uiManagerRef,
    youboraInitializedRef,
    playerType,
    streamUrl,
    VODAsset,
    LinearAsset,
    isAdsEnabled,
    manageAudioProfile,
    onAdBeginUI,
    onFinish,
    onCompleted,
    onDestroy,
    onSeeked,
    onBuffering,
    onAudioTrackChange,
    setState,
    onError,
    onTransitionEnd,
    onPlayPause,
    onPlaying,
    clearConcurrentLock,
    onSubtitlesView,
    onAudioView,
    playerReadyCallback,
    sendBookmark,
    bookmarkedVodTimeCode,
    textTrackTimer,
    bookmarked,
    rewindItem,
    onLoadedata,
}: UsePlayerHandlersParams): UsePlayerHandlerResults => {
    const language = useLanguage();
    const selectedProfile = useSelector((state: IStore) => state.profile.selectedProfile);
    const { track } = useContext(MixpanelContext);
    const { kochava } = useContext(KochavaContext);

    const initializeYoubora = useCallback(
        (player) => {
            const isSupported = checkDRMSupported(player, onDestroy, setState);
            if (!isSupported) {
                return;
            }
            if (player && (LinearAsset || VODAsset) && !youboraInitializedRef.current) {
                const streamType =
                    {
                        [playerTypes.LiveTV]: playerTypes.LiveTV,
                        [playerTypes.Catchup]: playerTypes.Catchup,
                        [playerTypes.Vod]: playerTypes.Vod,
                        [playerTypes.Trailer]: playerTypes.Trailer,
                        [playerTypes.PlayTrailer]: playerTypes.PlayTrailer,
                    }[playerType] || playerTypes.Vod;

                setYBPluginOptions(player);

                const isToPlayFromBookmark =
                    playerType === playerTypes.Vod &&
                    typeof bookmarkedVodTimeCode === 'function' &&
                    bookmarkedVodTimeCode() !== 0;

                // TODO: refactor this after pushing it to production
                // use if else instead of tangled inline if else
                updateYouboraAnalytics(
                    playerType === playerTypes.LiveTV
                        ? { program: { ...LinearAsset }, channel: LinearAsset?.channel }
                        : playerType === playerTypes.Catchup
                        ? LinearAsset
                        : playerType === playerTypes.Vod ||
                          playerType === playerTypes.Trailer ||
                          playerType === playerTypes.PlayTrailer
                        ? VODAsset
                        : null,
                    streamUrl,
                    streamType,
                    undefined,
                    isAdsEnabled,
                    isToPlayFromBookmark,
                    playerSSAIManager.hasActiveAds
                );
                youboraInitializedRef.current = true;
            }
        },
        [LinearAsset, VODAsset, streamUrl, playerType, isAdsEnabled, bookmarkedVodTimeCode]
    );

    const handlePlayerRef = useMemo(() => {
        return (player: PlayerAPI) => {
            if (playerInitializedRef.current) return;

            if (player && playerService) {
                playerService.cancelDestroyPlayer();
                playerInstanceRef.current = player;
                initializeYoubora(player);
                try {
                    document
                        .querySelector('.bmpui-ui-controlbar')
                        ?.addEventListener('transitionend', () => onTransitionEnd(playerType));
                } catch (e) {
                    log.error(e);
                }

                if (onSubtitlesView) {
                    const subtitleControl = document.querySelector('.bmpui-ui-subtitleselectbox');
                    let isOpened = true;
                    const onSubtitleViewClick = () => {
                        isOpened = !isOpened;
                        onSubtitlesView({ menu: isOpened });
                    };
                    if (subtitleControl) {
                        subtitleControl.addEventListener('click', onSubtitleViewClick);
                    }
                }

                if (onAudioView) {
                    const audioControl = document.querySelector('.bmpui-ui-audiotrackselectbox');
                    let isOpened = true;
                    const onAudioViewClick = () => {
                        isOpened = !isOpened;
                        onAudioView({ menu: isOpened });
                    };
                    if (audioControl) {
                        audioControl.addEventListener('click', onAudioViewClick);
                    }
                }
            }
        };
    }, []);

    const handleFirstPlay = () => {
        const player: any = playerInstanceRef.current;
        if (!player || !isPlayerAlive(player)) return;

        if (selectedProfile.defaultSubTitleLang) {
            setSubtitleLanguage(player, selectedProfile.defaultSubTitleLang);
        }

        if (manageAudioProfile && selectedProfile.defaultAudioLang) {
            const storedAudioLang = sessionStorage.getItem('selectedAudioLang');
            if (storedAudioLang) {
                setAudioLanguage(player, storedAudioLang);
            } else {
                setAudioLanguage(player, selectedProfile.defaultAudioLang);
                sessionStorage.setItem('selectedAudioLang', selectedProfile.defaultAudioLang);
            }
        }

        if (playerType === playerTypes.Trailer) {
            const selectedVideoItem = VODAsset ? VODAsset : null;
            track(`VOD Player - ${EVENT.VOD_PLAYER.START}`, {
                ...getItemProperties(selectedVideoItem),
                'VOD Duration': f.converToHHMMSS(player?.getDuration()),
                'Video Type': playerTypes.Trailer,
            });
        }

        if (onPlaying) {
            onPlaying(playerType === playerTypes.LiveTV ? LinearAsset : VODAsset);
        }
        if (sendBookmark && playerType === playerTypes.LiveTV) {
            sendBookmark(LinearAsset);
        }
        if (kochava && playerType === playerTypes.LiveTV) {
            sendLiveTvKochava(kochava, { ...LinearAsset, currLang: language }, playerType);
        } else if (kochava && playerType === playerTypes.Vod) {
            sendKochava(kochava, VODAsset);
        }
    };

    const handlePlayerOnloadeddata = () => {
        // Hide Genesys chat when video type is not Trailer
        // Trailer type player does not take full screen so chat can be accessed.
        // if (playerType !== playerTypes.Trailer) {
        Genesys.hide();
        // }
        const player: any = playerInstanceRef.current;
        // The player may have been destroyed while its `load()` was in flight
        // (e.g. Live TV channel switch); using its API then throws API_NOT_AVAILABLE.
        if (!player || !isPlayerAlive(player)) return;
        if (playerReadyCallback) {
            playerReadyCallback(player);
        }
        onSuccess();

        if (bookmarkedVodTimeCode) {
            const time = Math.ceil(bookmarkedVodTimeCode());
            if (player.isLive()) {
                player.timeShift(time);
            } else {
                player.seek(time);
            }
        }
        safePlayerPlay(player);

        if (textTrackTimer) clearTimeout(textTrackTimer);
        textTrackTimer = setTimeout(() => {
            // Player may have been destroyed during the 1s wait (e.g. channel switch).
            if (!isPlayerAlive(player) || !selectedProfile.defaultSubTitleLang) return;
            setSubtitleLanguage(player, selectedProfile.defaultSubTitleLang);
        }, 1000);

        player['addEventListener'] = player.on;
        player['removeEventListener'] = player.off;
        playerService.setPlayer(player);
        cdnHandler.bindEvents(attachEvents(player));

        const bookmark = bookmarked && bookmarked();
        if (bookmark && player) {
            const timecode = parseInt(bookmark.timecode, 10) || 0;
            if (player.isLive()) {
                player.timeShift(timecode);
            } else {
                player.seek(timecode);
            }
        } else if (rewindItem) {
            const startTime = new Date(rewindItem.startTime).getTime();
            const timeToSeek =
                'recordingStartTime' in rewindItem
                    ? new Date(rewindItem.recordingStartTime).getTime() - startTime
                    : startTime;
            player.seek(timeToSeek ? (timeToSeek / 1000).toString() : '0');
        }
        if (onLoadedata) onLoadedata();
    };

    const handlePlayerTimeChanged = () => {
        const player: PlayerAPI = playerService.player;
        if (!player || !isPlayerAlive(player)) return;
        if (playerService.getAdsData() != null && isAdsEnabled) {
            onAdBeginUI(player, playerService.getAdsData());
        }
    };

    const handlePlayerOnFinish = () => {
        if (onFinish) onFinish();
        if (onCompleted) onCompleted();
    };

    const handlePlayerOnDestroy = () => {
        if (onDestroy) onDestroy();
    };

    const handlePlayerOnSeeked = () => {
        if (onSeeked) onSeeked();
    };

    const handleOnStallEnded = () => {
        const player = playerInstanceRef.current;
        if (player.getBufferedRanges().length === 0) {
            if (onBuffering) onBuffering(LinearAsset);
            return;
        }
        const bufferRange = player.getBufferedRanges()[0];
        const bufferedSeconds = (bufferRange.end - bufferRange.start) / 1000;
        if (bufferedSeconds <= 4 && onBuffering) {
            onBuffering(LinearAsset);
        }
    };

    const handleOnAudioTrackChange = (event) => {
        if (onAudioTrackChange) onAudioTrackChange(event);
    };

    const handleUIManagerReady = (uiManager: UIManager) => {
        uiManagerRef.current = uiManager;
        useLoadTooltips();
        uiManager.onActiveUiChanged.subscribe(() => useLoadTooltips());
    };

    const handlePlayerOnError = (event) => {
        if (event) {
            console.warn(new Error('Error while playing'), event);
        }

        const errorKey = 'player_err_' + event?.name.toLowerCase();
        const title = __(errorKey + '_title');
        const desc = __(errorKey + '_desc');
        handleError(
            {
                title: title === errorKey + '_title' ? __('general_error_message') : title,
                description: desc === errorKey + '_desc' ? event.error : desc + ` (${event.code})`,
            },
            onDestroy,
            setState
        );

        clearConcurrentLock();
        if (onError) onError(errorKey, event.error);
        try {
            remoteLogger()?.player(event);
        } catch (e) {
            log.error(e);
        }
    };

    useEffect(() => {
        useLoadTooltips();
    }, [language]);

    return {
        handlePlayerRef,
        handlePlayerOnloadeddata,
        handleFirstPlay,
        handlePlayerTimeChanged,
        handlePlayerOnFinish,
        handlePlayerOnDestroy,
        handlePlayerOnSeeked,
        handleOnStallEnded,
        handleOnAudioTrackChange,
        handleUIManagerReady,
        handlePlayerOnError,
    };
};
